import { C1_CONTRACT, compileCreatureEquippedCounts } from './possessor-compile';
import { recompileArmorShieldAc, type ArmorShieldAcResult } from './creature-ac-slots';
import { AC_DEFINITION_NOT_HELD } from './creature-armor-class';
import { statusProjectionDependencies } from './status-attack-budget';
import { createFormulaReadContext } from './formula-read-context';
import { createEquippedIntegerCompiler } from './equipped-integer-contribution';
import { createComputeSourceCleaner } from './compute-source-cleanup';
import { computeSourceBreakdown } from './computed-persistence';
import { readInventoryField, readInventoryRows, readItemUuidSlot } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { readItemNumericSlot } from './enchantment-template';
import type { EquipPlanRow } from '../inventory/equip-plan';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => readItemUuidSlot(value).toUpperCase();

/** A read-only decision view borrows the held graph, owning only changed inventory columns. */
export function equipmentAcDecisionView(row: Row, changes: readonly EquipPlanRow[]): Row {
    const byId = new Map(changes.map(change => [change.inventory_id.toUpperCase(), change]));
    const inventory = readInventoryRows(row, 'inventory').map(entry => {
        const change = byId.get(upper(readInventoryField(entry, 'inventory_id')));
        if (!change) return entry;
        const bucket = record(entry.inventory);
        if (!bucket) return entry;
        const changedBucket = { ...bucket, equipped: change.equipped };
        return new Proxy(entry, { get: (target, key, receiver) => key === 'inventory' ? changedBucket : Reflect.get(target, key, receiver) });
    });
    return new Proxy(row, { get: (target, key, receiver) => key === 'inventory' ? inventory : Reflect.get(target, key, receiver) });
}

/** Reuse the native slot/gear kernels; only the actor's AC cluster is derived. */
export function projectEquipmentArmorClass(row: Row, definition: (id: string) => Row | null): ArmorShieldAcResult {
    let missing: string | undefined;
    try {
        const deps = statusProjectionDependencies(row, definition), readers = createFormulaReadContext(deps);
        const creature = record(record(row.computed)?.creature), prior = record(creature?.ac);
        if (!prior) return { ok: false, reason: 'unported:ac:prior-shape' };
        const requireDefinition = (id: string): Row => {
            const def = definition(id);
            if (!def) { missing = id; throw new Error(AC_DEFINITION_NOT_HELD); }
            return def;
        };
        for (const item of readInventoryRows(row, 'inventory'))
            if (upper(readInventoryField(item, 'equipped'))) requireDefinition(upper(readInventoryField(item, 'inventory_item_id')));
        const naturalId = upper(readers.layerValue(row, 'natural_armor'));
        if (naturalId) requireDefinition(naturalId);
        const equipped = compileCreatureEquippedCounts(row, { definition, catalog: record(row.catalog) ?? undefined }, { retainEmpty: true });
        if (!equipped) return { ok: false, reason: 'unported:ac:equipped-shape' };
        const slot = (id: string, cap: boolean) => {
            if (!id) return null;
            const def = requireDefinition(id);
            const limit = cap ? readers.layerValue(def, 'max_body_ac_mod') : null;
            return { id, name: String(readItemField(def, 'item_name') ?? ''), acMod: readItemNumericSlot(readers.layerValue(def, 'ac_mod') ?? 0, NaN),
                maxBodyAcMod: limit == null ? null : readItemNumericSlot(limit, NaN) };
        };
        const armorId = upper(equipped.equipped_armor_inventory_item_id), shieldId = upper(equipped.equipped_shield_inventory_item_id);
        const natural = armorId && readItemField(requireDefinition(armorId), 'item_type') === 'natural_armor';
        const cleaner = createComputeSourceCleaner(deps), gear = createEquippedIntegerCompiler({ ...deps, metadata: C1_CONTRACT.rollup });
        const contribution = (family: string) => { const value = gear.contribution(row, family); return { total: value.total, sources: value.sources.map(source => ({ ...source })) }; };
        // Source rows retain the prior slot identities after equipment flags have changed.
        const sources = record(prior.ac)?.sources;
        const priorSlots = (Array.isArray(sources) ? sources : []).filter(record).filter(source => source.category === 'inventory_equipped'
            && source.kind !== 'inventory_equipped');
        const priorId = (kind: string): string => upper(priorSlots.find(source => source.kind === kind
            || !source.kind && source.href_id && readItemField(requireDefinition(upper(source.href_id)), 'item_type') === kind)?.href_id);
        const body = readItemNumericSlot(readers.layerValue(row, 'body') ?? 0, NaN);
        if (!Number.isInteger(body)) return { ok: false, reason: 'unported:ac:body-shape' };
        const result = recompileArmorShieldAc({ priorAc: prior, priorDamageMitigation: creature?.damage_mitigation,
            body, priorArmorId: priorId('armor'), priorShieldId: priorId('shield'),
            armor: natural ? null : slot(armorId, true), shield: slot(shieldId, false),
            naturalArmor: natural ? { id: armorId, acMod: readItemNumericSlot(readers.layerValue(requireDefinition(armorId), 'ac_mod') ?? 0, NaN) } : null,
            gear: { magical: contribution('ac_mod_magical'), nonMagical: contribution('ac_mod_non_magical'), dm: contribution('damage_mitigation') },
            clean: sources => Object.values(cleaner.clean(sources, row)) as Row[], typeOf: id => String(deps.queryItemType(id)),
            breakdown: sources => computeSourceBreakdown(sources) as Row });
        if (result.ok && readItemField(record(row.encounter_creatures), 'item_type') !== 'player_character') {
            // NPC readers use ac.mitigation; a partial mirror would hide its status/host share.
            const priorMirror = record(creature?.damage_mitigation);
            return { ...result, damageMitigation: result.ac.mitigation === undefined && priorMirror?.total === 0 ? priorMirror : undefined };
        }
        return result;
    } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        const absent = missing ?? /^def-missing:([0-9a-f-]{36})$/i.exec(reason)?.[1];
        return { ok: false, reason: absent || reason.includes(AC_DEFINITION_NOT_HELD) ? AC_DEFINITION_NOT_HELD : `unported:ac:equipment:${reason}`,
            ...(absent ? { missing: [absent] } : {}) };
    }
}

/** Preserve held cluster identity, applying only values the slot kernel changed. */
export function installEquipmentArmorClass(row: Row, result: Extract<ArmorShieldAcResult, { ok: true }>): void {
    const creature = record(record(row.computed)?.creature), ac = record(creature?.ac);
    if (!creature || !ac) return;
    for (const key of Object.keys(ac)) if (!(key in result.ac)) delete ac[key];
    for (const [key, value] of Object.entries(result.ac)) {
        if (JSON.stringify(ac[key]) !== JSON.stringify(value)) ac[key] = value;
    }
    if (result.damageMitigation === undefined) delete creature.damage_mitigation;
    else if (JSON.stringify(creature.damage_mitigation) !== JSON.stringify(result.damageMitigation)) creature.damage_mitigation = result.damageMitigation;
}
